import type { ComponentProps, ComponentType, MouseEvent, ReactNode } from 'react';
import Tooltip from '@/components/ui/Tooltip';
import { XIcon } from '@phosphor-icons/react';

/**
 * Shared visual primitive for inline "chip" links used throughout the
 * chat stream. Both ``ResourceLink`` (``<resource id='N'>…</resource>``
 * citations) and ``FileMessageItem`` (uploaded user files) render on
 * top of this component so the two stay pixel-identical — an icon plus
 * an underlined filename/title rendered inline with the surrounding
 * text.
 *
 * Consumers own the click behaviour (route navigation, analytics, etc.)
 * and just hand us an icon, label, href, and optional ``tone`` /
 * ``iconSpin`` / ``disabled`` / ``onDelete`` flags for error, loading,
 * and deletable states.
 *
 * When ``onDelete`` is provided:
 * - The icon swaps to a × delete button on hover (CSS ``group`` trick,
 *   no JS state needed).
 * - The label ``<a>`` / ``<span>`` still works normally — clicking the
 *   label follows the href; clicking the icon area triggers deletion.
 */

// Phosphor icon type — exported so consumers can type their icon props
// without duplicating the declaration.
export type IconComponent = ComponentType<
  ComponentProps<'svg'> & {
    size?: number | string;
    weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
  }
>;

export interface LinkChipProps {
  icon: IconComponent;
  /** Spin the icon in place — use for in-flight loading states. */
  iconSpin?: boolean;
  /** Visible text (usually a resource title or filename). */
  label: ReactNode;
  /**
   * Destination URL. When omitted the label is rendered as a ``<span>``
   * rather than an ``<a>`` tag — useful for non-navigable chips such as
   * inline resource tags in the chat input.
   */
  href?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /**
   * Tooltip content. Used as the native browser ``title`` on the anchor
   * when the chip is interactive, and escalated to a real MUI ``Tooltip``
   * when ``disabled`` is true — native ``title`` doesn't reliably fire on
   * elements with ``pointer-events-none``, so we need the JS tooltip to
   * actually explain why the chip is inert.
   */
  title?: string;
  /** Open in a new tab. */
  newTab?: boolean;
  /** Render in a non-interactive style (cursor, opacity, blocks clicks).
   * Also suppresses the browser's URL preview on hover, because
   * ``pointer-events-none`` is applied to the anchor. */
  disabled?: boolean;
  /** Colour palette. ``'blue'`` (the default) for regular links;
   * ``'red'`` for error states. */
  tone?: 'blue' | 'red';
  /**
   * When provided, hovering the chip replaces the icon with a × delete
   * button. Clicking that button calls this handler (propagation is
   * stopped so it doesn't trigger the label's link handler).
   */
  onDelete?: () => void;
  /**
   * Maximum number of characters to display from a string ``label``.
   * When the label exceeds this length it is truncated with an ellipsis
   * and the full text is shown in the chip's ``title`` tooltip.
   * Has no effect when ``label`` is not a string.
   */
  maxLabelLength?: number;
}

const TONE_STYLES: Record<NonNullable<LinkChipProps['tone']>, { text: string; decoration: string }> = {
  blue: { text: 'text-blue-800', decoration: 'decoration-blue-400' },
  red: { text: 'text-red-600', decoration: 'decoration-red-400' }
};

export function LinkChip({
  icon: Icon,
  iconSpin = false,
  label,
  href,
  onClick,
  title,
  newTab = false,
  disabled = false,
  tone = 'blue',
  onDelete,
  maxLabelLength
}: LinkChipProps) {
  const palette = TONE_STYLES[tone];

  // Apply truncation to string labels only.
  const truncatedLabel =
    maxLabelLength && typeof label === 'string' && label.length > maxLabelLength
      ? label.slice(0, maxLabelLength) + '…'
      : label;
  // Expose full text as tooltip when truncated (unless caller already set one).
  const resolvedTitle = title ?? (truncatedLabel !== label && typeof label === 'string' ? label : undefined);

  // The outer ``span`` and inner ``a`` each need cursor styling — an
  // ``<a>`` with an ``href`` always paints its own default link cursor,
  // which would otherwise override ``cursor-not-allowed`` from the
  // parent. Disabling pointer events on the anchor also prevents
  // ``hover:bg-bluegray-300`` from firing, blocks clicks, and stops the
  // browser from previewing ``href`` in the status bar.
  const wrapperClass = disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-bluegray-300';
  const anchorClass = disabled ? 'cursor-not-allowed pointer-events-none' : '';

  // Icon slot: when deletable, the icon swaps for a × button on hover.
  // Both elements are sized identically (19 px) so the layout doesn't
  // shift. The swap is pure CSS via Tailwind's ``group`` / ``group-hover``
  // utilities — no JS state required.
  // Icon slot: when deletable the icon fades to a × delete button on
  // hover. The container is ``inline-block`` at the same 19 px as the
  // icon, with ``verticalAlign: 'text-bottom'`` so it sits on the same
  // baseline as surrounding text — matching the original ``inline`` icon.
  const iconSlot = onDelete ? (
    <span
      className="relative inline-block mr-1 flex-shrink-0"
      style={{ width: 19, height: 19, verticalAlign: 'text-bottom' }}
    >
      <Icon size={19} weight="regular" className="absolute inset-0 group-hover:opacity-0 transition-opacity" />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-sm text-blue-800 focus:outline-none"
        aria-label="Remove"
        tabIndex={-1}
      >
        <XIcon size={15} weight="bold" />
      </button>
    </span>
  ) : (
    <Icon size={19} weight="regular" className={`inline mr-1 flex-shrink-0 mb-0.5 ${iconSpin ? 'animate-spin' : ''}`} />
  );

  // Split a string label so [icon] + [first word] can never break across
  // lines (it looks broken when an icon ends up alone on the previous
  // line) while subsequent words flow normally and wrap to fit narrow
  // containers — important for chat bubbles where long resource titles
  // would otherwise overflow horizontally.  When the label isn't a plain
  // string we can't split it, so the whole thing stays glued to the icon.
  const labelIsString = typeof truncatedLabel === 'string';
  const labelStr = labelIsString ? (truncatedLabel as string) : '';
  const firstSpaceIdx = labelIsString ? labelStr.search(/\s/) : -1;
  const labelHead: ReactNode = labelIsString
    ? firstSpaceIdx === -1
      ? labelStr
      : labelStr.slice(0, firstSpaceIdx)
    : truncatedLabel;
  // Keep the leading whitespace on the tail so the visual gap between
  // the first word and the rest is preserved exactly (and the underline
  // runs continuously through it).
  const labelTail: string = labelIsString && firstSpaceIdx !== -1 ? labelStr.slice(firstSpaceIdx) : '';

  const linkClass = `font-semibold text-inherit underline underline-offset-2 ${palette.decoration} ${anchorClass}`;
  const renderLinkSegment = (text: ReactNode, key?: string) =>
    href ? (
      <a
        key={key}
        href={href}
        onClick={onClick}
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noopener noreferrer' : undefined}
        title={disabled ? undefined : resolvedTitle}
        className={linkClass}
      >
        {text}
      </a>
    ) : (
      <span key={key} className="font-semibold" title={resolvedTitle}>
        {text}
      </span>
    );

  // Keep outer wrapper as a plain inline ``<span>`` — ``inline-flex``
  // breaks text-baseline alignment with the surrounding prose.  Note we
  // intentionally do NOT apply ``whitespace-nowrap`` here: the inner
  // ``<span class="whitespace-nowrap">`` only protects the icon + first
  // word so the rest of the label can wrap onto the next line.
  const chip = (
    <span className={`group pr-0.5 rounded-sm ${palette.text} ${wrapperClass}`}>
      <span className="whitespace-nowrap">
        {iconSlot}
        {renderLinkSegment(labelHead, 'head')}
      </span>
      {labelTail && renderLinkSegment(labelTail, 'tail')}
    </span>
  );

  if (disabled && resolvedTitle) {
    // ``pointer-events-none`` on the anchor suppresses the browser's
    // native tooltip, so we use MUI to explain why the chip is inert.
    return (
      <Tooltip title={resolvedTitle} enterDelay={0} leaveDelay={0}>
        {chip}
      </Tooltip>
    );
  }
  return chip;
}
